import { FC, ReactNode } from 'react';

import CheckIcon from '../assets/li_check.svg';
import { useMergedConfig } from '../context/MergedConfigProvider';
import { usePlatformAutoLogin } from '../hooks/usePlatformAutoLogin';
import { useTranslations } from '../hooks/useTranslations.ts';
import { translationKeys } from '../translations/translationKeys.ts';
import { returnToCustomerHub } from '../utils/navigation.ts';
import { setPlatformLanguage, setPlatformUrlParam } from '../utils/platformUrl.ts';
import { useIsMobile } from '../utils/useIsMobile';
import { BaseScreen } from './BaseScreen';

const PLATFORM_LINK = import.meta.env.VITE_PLATFORM_URL_MAP;
const APP_ONE_LINK = import.meta.env.VITE_APP_ONE_LINK;

export type ThankYouScreenProps = {
  embedded: boolean;
  csrfToken: string;
  context?: 'Live' | 'Demo';
  userEmail?: string;
  userPassword?: string;
};

type WrapperProps = ThankYouScreenProps & {
  title: string;
  disableOnMobile?: boolean;
  children: ReactNode;
};

const ThankYouScreenWrapper: FC<WrapperProps> = ({
  embedded,
  csrfToken,
  title,
  disableOnMobile = false,
  userEmail,
  userPassword,
  children,
}) => {
  const { translate } = useTranslations();
  const isMobile = useIsMobile();
  const { iaid, launchedInNewTab, lang, region } = useMergedConfig();

  let platformLink = setPlatformLanguage(PLATFORM_LINK, lang);
  if (iaid) {
    platformLink = setPlatformUrlParam(platformLink, 'iaid', iaid);
  }
  if (region) {
    platformLink = setPlatformUrlParam(platformLink, 'r', region);
  }
  const { isSubmitting, onSubmit } = usePlatformAutoLogin({
    embedded,
    isMobile,
    csrfToken,
    platformLink,
    appOneLink: APP_ONE_LINK,
    userEmail,
    userPassword,
  });

  const isDisabled = disableOnMobile && isMobile;

  const handleClose = () => {
    if (launchedInNewTab) {
      returnToCustomerHub(embedded);
    }

    if (isMobile) {
      window.location.href = APP_ONE_LINK;
      return;
    }

    window.close();
  };

  return (
    <BaseScreen
      formTitle={title}
      formSubtitle=""
      headerIcon={CheckIcon}
      isThankYou={true}
      footerBtnLabel={
        isDisabled
          ? translate(translationKeys.NOT_AVAILABLE_MOBILE)
          : launchedInNewTab
            ? translate(translationKeys.THANK_YOU_BUTTON_CLOSE)
            : embedded
              ? translate(translationKeys.FINISH)
              : translate(translationKeys.THANK_YOU_BUTTON_LOG_IN)
      }
      footerBtnOnClick={launchedInNewTab ? handleClose : onSubmit}
      footerBtnLoading={isSubmitting}
      footerBtnDisabled={isDisabled || isSubmitting}
    >
      {children}
    </BaseScreen>
  );
};

export default ThankYouScreenWrapper;
